"use client";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Zap,
  Cog,
  Activity,
  ScalingIcon,
  Smile,
} from "lucide-react";

const reasons = [
  {
    icon: ShieldCheck,
    title: "Secure Infrastructure",
    desc: "Hardened bot pipelines with encrypted secrets, scoped tokens and audit logs.",
    span: "md:col-span-2",
  },
  {
    icon: Zap,
    title: "Fast Performance",
    desc: "Sub-100ms response times with edge-cached handlers.",
    span: "md:col-span-2",
  },
  {
    icon: Cog,
    title: "Modern Automation",
    desc: "Composable workflows, schedulers & event-driven hooks.",
    span: "md:col-span-2",
  },
  {
    icon: Activity,
    title: "24/7 Reliability",
    desc: "Self-healing services, multi-region failover, status monitors.",
    span: "md:col-span-3",
  },
  {
    icon: ScalingIcon,
    title: "Scalable Systems",
    desc: "Horizontally scaled queues — from 100 to 1M users without rewrites.",
    span: "md:col-span-3",
  },
  {
    icon: Smile,
    title: "User-Friendly",
    desc: "Conversational UX patterns designed for non-technical members.",
    span: "md:col-span-6",
  },
];

export default function WhyChoose() {
  return (
    <section id="why" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5" data-testid="why-label">
            Why Axion Bots
          </p>
          <h2
            className="font-display text-4xl md:text-5xl font-light tracking-tight"
            data-testid="why-heading"
          >
            Engineered for{" "}
            <span className="gradient-text font-medium">trust, scale and delight</span>.
          </h2>
          <p className="mt-5 text-neutral-400">
            Six pillars that make us the studio of choice for serious Telegram product
            teams and community owners.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.06, duration: 0.55 }}
              className={`group relative rounded-2xl p-7 bg-[#0A0A0A] border border-white/[0.08] hover:border-white/20 transition-all duration-300 overflow-hidden ${r.span}`}
              data-testid={`why-card-${i}`}
            >
              <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#00E5FF]/0 group-hover:bg-[#00E5FF]/10 blur-3xl transition-all duration-500" />
              <div className="relative">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#007AFF]/15 to-[#00E5FF]/10 border border-white/10 grid place-items-center">
                  <r.icon className="h-5 w-5 text-[#7CCBF1]" />
                </div>
                <h3 className="mt-6 font-display text-lg md:text-xl font-medium">
                  {r.title}
                </h3>
                <p className="mt-2 text-sm text-neutral-400 leading-relaxed max-w-md">
                  {r.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
